import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Heart, ShoppingCart, Truck, RefreshCw, Shield, Star, ChevronRight, Minus, Plus, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../CartContext';
import { FREE_SHIPPING_THRESHOLD, RETURN_DAYS, formatTl } from '../constants/storePolicies';
import { HavaleTrustBlock } from '../components/HavaleTrustBlock';
import { SeoHead } from '../components/SeoHead';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://kiyafet-magazasi-backend.vercel.app';
const SITE = 'https://www.ladiora.com.tr';

interface Review {
  id: number;
  yazar: string;
  puan: number;
  yorum: string;
  beden: string;
  created_at: string;
}

export function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState<any>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState<any>(null);
  const [quantity, setQuantity] = useState(1);
  const [isLiked, setIsLiked] = useState(false);
  const [newReview, setNewReview] = useState({ yazar: '', puan: 5, yorum: '', beden: '' });
  const [showReviewForm, setShowReviewForm] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/urunler/${id}`).then(r => r.json()),
      fetch(`${API_URL}/api/yorumlar/${id}`).then(r => r.json()).catch(() => [])
    ]).then(([urun, yorumlar]) => {
      setProduct(urun);
      setReviews(Array.isArray(yorumlar) ? yorumlar : []);
      setLoading(false);
    }).catch(() => { setLoading(false); });

    // Favori durumunu kontrol et
    const kullanici = JSON.parse(localStorage.getItem('kullanici') || 'null');
    if (kullanici) {
      fetch(`${API_URL}/api/favorilerim/${kullanici.id}`)
        .then(r => r.json())
        .then(favs => {
          if (Array.isArray(favs) && favs.some((f: any) => f.id === Number(id))) {
            setIsLiked(true);
          }
        }).catch(() => {});
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div><p className="text-gray-600">Yükleniyor...</p></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product || product.mesaj) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center"><p className="text-gray-600 text-xl">Ürün bulunamadı</p></main>
        <Footer />
      </div>
    );
  }

  const images = product.resimler && product.resimler.length > 0 ? product.resimler : ['https://via.placeholder.com/600x800?text=Resim+Yok'];
  const sizes = product.beden && product.beden.length > 0 ? product.beden : ['S', 'M', 'L', 'XL'];
  const colorMap: Record<string, string> = {
    'siyah': '#000000', 'beyaz': '#ffffff', 'kırmızı': '#dc2626', 'pembe': '#ec4899',
    'mavi': '#3b82f6', 'lacivert': '#000080', 'yeşil': '#16a34a', 'sarı': '#eab308',
    'turuncu': '#f97316', 'mor': '#9333ea', 'gri': '#6b7280', 'kahverengi': '#92400e',
    'bej': '#d4a574', 'bordo': '#800020', 'krem': '#fffdd0', 'altın': '#d4af37',
    'gümüş': '#c0c0c0', 'haki': '#c3b091', 'ekru': '#f5f0e1', 'pudra': '#f4c2c2',
    'fuşya': '#ff00ff', 'lila': '#c8a2c8', 'mint': '#98ff98', 'mercan': '#ff7f50',
    'taş': '#b8a99a', 'antrasit': '#383838', 'camel': '#c19a6b', 'vizon': '#8b7d6b',
    'ten': '#e8beac', 'nude': '#e8beac', 'hardal': '#c49102', 'petrol': '#006064',
    'indigo': '#4b0082', 'turkuaz': '#40e0d0', 'kiremit': '#cb4154', 'taba': '#a0522d'
  };
  const getColorHex = (name: string) => colorMap[name.toLowerCase().trim()] || '#d4a574';
  const colors = product.renk && product.renk.length > 0
    ? product.renk.map((r: string) => ({ name: r, hex: getColorHex(r) }))
    : [{ name: 'Standart', hex: '#d4a574' }];
  const stock = product.stokMiktari ?? product.stok_miktari ?? (product.stokDurumu !== false && product.stok_durumu !== false ? 15 : 0);
  const bedenStok: Record<string, number> = product.bedenStok || product.beden_stok || {};
  const getSizeStock = (size: string) => {
    if (Object.keys(bedenStok).length > 0) return bedenStok[size] ?? 0;
    return stock; // beden bazlı stok yoksa toplam stoku kullan
  };
  const rating = reviews.length > 0 ? (reviews.reduce((s: number, r: Review) => s + r.puan, 0) / reviews.length) : 0;

  const handleAddToCart = () => {
    if (!selectedSize) { toast.error('Lütfen beden seçiniz'); return; }
    const sizeStock = getSizeStock(selectedSize);
    if (sizeStock <= 0) { toast.error('Bu beden tükendi'); return; }
    addToCart({
      id: product.id,
      name: product.ad,
      price: product.fiyat,
      image: images[0],
      size: selectedSize,
      color: selectedColor?.name || colors[0]?.name
    }, sizeStock);
    setTimeout(() => navigate('/sepet'), 1000);
  };

  const handleLike = async () => {
    const kullanici = JSON.parse(localStorage.getItem('kullanici') || 'null');
    if (!kullanici) { toast.error('Favori eklemek için giriş yapın'); navigate('/hesabim'); return; }
    try {
      if (isLiked) {
        await fetch(`${API_URL}/api/favori-cikar/${kullanici.id}/${product.id}`, { method: 'DELETE' });
        setIsLiked(false);
        toast.success('Favorilerden çıkarıldı');
      } else {
        await fetch(`${API_URL}/api/favori-ekle`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ kullaniciId: kullanici.id, urunId: product.id })
        });
        setIsLiked(true);
        toast.success('Favorilere eklendi!');
      }
    } catch { toast.error('Bir hata oluştu'); }
  };

  const handleSubmitReview = async () => {
    if (!newReview.yazar || !newReview.yorum) { toast.error('Lütfen tüm alanları doldurun'); return; }
    try {
      const res = await fetch(`${API_URL}/api/yorum`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urunId: product.id, yazar: newReview.yazar, puan: newReview.puan, yorum: newReview.yorum, beden: newReview.beden })
      });
      const data = await res.json();
      if (data.basarili) {
        setReviews([data.yorum, ...reviews]);
        setNewReview({ yazar: '', puan: 5, yorum: '', beden: '' });
        setShowReviewForm(false);
        toast.success('Yorumunuz eklendi!');
      }
    } catch { toast.error('Yorum eklenemedi'); }
  };

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.ad,
    description: (product.aciklama || product.ad || '').slice(0, 300),
    image: images,
    sku: String(product.id),
    brand: { '@type': 'Brand', name: product.marka || 'Ladiora Boutique' },
    offers: {
      '@type': 'Offer',
      url: `${SITE}/urun/${product.id}`,
      priceCurrency: 'TRY',
      price: Number(product.fiyat || 0).toFixed(2),
      availability: stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    },
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SeoHead
        title={product.seoTitle || product.seo_title || `${product.ad} | Ladiora Boutique`}
        description={product.seoDescription || product.seo_description || (product.aciklama || `${product.ad} — Ladiora Boutique`).slice(0, 155)}
        image={images[0]}
        jsonLd={productJsonLd}
      />
      <Header />

      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <a href="/" className="hover:text-pink-600">Ana Sayfa</a>
              <ChevronRight className="size-4" />
              <a href={`/kategori/${String(product.kategori || '').toLowerCase().replace(/ğ/g,'g').replace(/ü/g,'u').replace(/ş/g,'s').replace(/ı/g,'i').replace(/ö/g,'o').replace(/ç/g,'c').replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'')}`} className="hover:text-pink-600">{product.kategori}</a>
              <ChevronRight className="size-4" />
              <span className="text-gray-900 font-medium">{product.ad}</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-16">
            {/* Left Column - Images */}
            <div>
              <div className="relative aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden mb-4">
                <img src={images[selectedImage]} alt={product.ad} className="w-full h-full object-cover" />
                {product.eski_fiyat && product.eski_fiyat > product.fiyat && (
                  <div className="absolute top-4 left-4 bg-pink-600 text-white px-3 py-1 rounded-full text-sm font-bold">İNDİRİM</div>
                )}
              </div>
              <div className="grid grid-cols-4 gap-3">
                {images.map((image: string, index: number) => (
                  <button key={index} onClick={() => setSelectedImage(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${selectedImage === index ? 'border-pink-600 scale-95' : 'border-gray-200 hover:border-pink-300'}`}>
                    <img src={image} alt={`${product.ad} ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Right Column - Details */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3">{product.ad}</h1>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`size-5 ${i < Math.floor(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                  ))}
                </div>
                <span className="text-gray-600">{rating.toFixed(1)} ({reviews.length} değerlendirme)</span>
              </div>

              {/* Price */}
              <div className="flex items-center gap-3 mb-6">
                <span className="text-4xl font-bold text-pink-600">₺{Number(product.fiyat).toLocaleString('tr-TR')}</span>
                {product.eski_fiyat && product.eski_fiyat > product.fiyat && (
                  <span className="text-2xl text-gray-400 line-through">₺{Number(product.eski_fiyat).toLocaleString('tr-TR')}</span>
                )}
              </div>

              {/* Description */}
              <p className="text-gray-600 mb-6 leading-relaxed">{product.aciklama || 'Zarif ve şık tasarımıyla öne çıkan premium ürünümüz.'}</p>

              {/* Color Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">Renk</label>
                <div className="flex gap-2">
                  {colors.map((color: any) => (
                    <button key={color.name} onClick={() => setSelectedColor(color)}
                      className={`size-10 rounded-full border-2 transition-all ${(selectedColor || colors[0])?.name === color.name ? 'border-pink-600 scale-110' : 'border-gray-300 hover:border-pink-300'}`}
                      style={{ backgroundColor: color.hex, boxShadow: color.hex === '#ffffff' || color.hex === '#fffdd0' || color.hex === '#f5f0e1' ? 'inset 0 0 0 1px #d1d5db' : 'none' }} title={color.name} />
                  ))}
                </div>
              </div>

              {/* Size Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Beden Seçimi {!selectedSize && <span className="text-pink-600">*</span>}
                </label>
                <div className="flex gap-2">
                  {sizes.map((size: string) => {
                    const sizeStk = getSizeStock(size);
                    const outOfStock = sizeStk <= 0 && Object.keys(bedenStok).length > 0;
                    return (
                      <button key={size} onClick={() => !outOfStock && setSelectedSize(size)} disabled={outOfStock}
                        className={`px-6 py-3 border-2 rounded-lg font-semibold transition-all ${outOfStock ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-50 line-through' : selectedSize === size ? 'border-pink-600 bg-pink-600 text-white' : 'border-gray-300 hover:border-pink-300 text-gray-700'}`}>
                        {size}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">Adet</label>
                <div className="flex items-center gap-3">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="size-10 border-2 border-gray-300 rounded-lg flex items-center justify-center hover:border-pink-300 transition-colors"><Minus className="size-4" /></button>
                  <span className="text-xl font-semibold w-12 text-center">{quantity}</span>
                  <button onClick={() => { const maxStk = selectedSize ? getSizeStock(selectedSize) : stock; setQuantity(Math.min(maxStk, quantity + 1)); }} className="size-10 border-2 border-gray-300 rounded-lg flex items-center justify-center hover:border-pink-300 transition-colors"><Plus className="size-4" /></button>
                  <span className="text-sm text-gray-500">(Stokta {selectedSize ? getSizeStock(selectedSize) : stock} adet)</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mb-8">
                <button onClick={handleAddToCart} disabled={stock <= 0} className={`flex-1 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${stock <= 0 ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-pink-600 text-white hover:bg-pink-700'}`}>
                  <ShoppingCart className="size-5" />{stock <= 0 ? 'Stokta Yok' : 'Sepete Ekle'}
                </button>
                <button onClick={handleLike} className={`size-14 border-2 rounded-lg flex items-center justify-center transition-all ${isLiked ? 'border-pink-600 bg-pink-50' : 'border-gray-300 hover:border-pink-300'}`}>
                  <Heart className={`size-6 ${isLiked ? 'fill-pink-600 text-pink-600' : 'text-gray-700'}`} />
                </button>
                <button className="size-14 border-2 border-gray-300 rounded-lg flex items-center justify-center hover:border-pink-300 transition-colors">
                  <Share2 className="size-5 text-gray-700" />
                </button>
              </div>

              {/* Features */}
              <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b">
                <div className="flex flex-col items-center text-center">
                  <div className="size-12 bg-pink-50 rounded-full flex items-center justify-center mb-2"><Truck className="size-6 text-pink-600" /></div>
                  <span className="text-sm font-medium text-gray-900">Ücretsiz Kargo</span><span className="text-xs text-gray-500">{formatTl(FREE_SHIPPING_THRESHOLD)} TL üzeri</span>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="size-12 bg-pink-50 rounded-full flex items-center justify-center mb-2"><RefreshCw className="size-6 text-pink-600" /></div>
                  <span className="text-sm font-medium text-gray-900">Kolay İade</span><span className="text-xs text-gray-500">{RETURN_DAYS} gün içinde</span>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="size-12 bg-pink-50 rounded-full flex items-center justify-center mb-2"><Shield className="size-6 text-pink-600" /></div>
                  <span className="text-sm font-medium text-gray-900">WhatsApp Sipariş</span><span className="text-xs text-gray-500">Havale/EFT</span>
                </div>
              </div>

              <div className="mb-8">
                <HavaleTrustBlock compact />
              </div>

              {/* Product Details */}
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Ürün Özellikleri</h3>
                  <ul className="space-y-1">
                    {(product.ozellikler && product.ozellikler.length > 0 ? product.ozellikler : ['Premium kalite kumaş', 'Nefes alabilen yapı', 'Kolay bakım', 'Türkiye üretimi']).map((ozellik: string, i: number) => (
                      <li key={i} className="text-sm text-gray-600 flex items-center gap-2"><span className="size-1.5 bg-pink-600 rounded-full"></span>{ozellik}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Reviews Section */}
          <div className="border-t pt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Müşteri Yorumları ({reviews.length})</h2>
              <button onClick={() => setShowReviewForm(!showReviewForm)} className="bg-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-pink-700 transition-colors">Yorum Yaz</button>
            </div>

            {showReviewForm && (
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <h3 className="font-semibold text-gray-900 mb-4">Yorum Ekle</h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <input type="text" placeholder="Adınız" value={newReview.yazar} onChange={e => setNewReview({...newReview, yazar: e.target.value})} className="px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" />
                  <input type="text" placeholder="Beden (opsiyonel)" value={newReview.beden} onChange={e => setNewReview({...newReview, beden: e.target.value})} className="px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" />
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm font-semibold text-gray-900">Puan:</span>
                  {[1,2,3,4,5].map(p => (
                    <button key={p} onClick={() => setNewReview({...newReview, puan: p})}>
                      <Star className={`size-6 ${p <= newReview.puan ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                    </button>
                  ))}
                </div>
                <textarea placeholder="Yorumunuz..." value={newReview.yorum} onChange={e => setNewReview({...newReview, yorum: e.target.value})} rows={3} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500 mb-4" />
                <button onClick={handleSubmitReview} className="bg-pink-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors">Gönder</button>
              </div>
            )}

            <div className="grid gap-6 mb-8">
              {reviews.map((review) => (
                <div key={review.id} className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">{review.yazar}</span>
                        <span className="text-sm text-gray-500">• {new Date(review.created_at).toLocaleDateString('tr-TR')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`size-4 ${i < review.puan ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                        {review.beden && <span className="text-sm text-gray-500">Beden: {review.beden}</span>}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700">{review.yorum}</p>
                </div>
              ))}
              {reviews.length === 0 && <p className="text-gray-500 text-center py-8">Henüz yorum yapılmamış. İlk yorumu siz yapın!</p>}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
